import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Users, Package, Calendar, MessageSquare, Star, DollarSign, Search, 
  Shield, ShieldCheck, ShieldX, Trash2, Eye, CheckCircle, XCircle,
  TrendingUp, AlertTriangle, Settings, ChevronLeft, ChevronRight, Mail, MapPin, Download,
  Wallet, Send, Clock, ArrowRight, RotateCcw, Award
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getImageUrl } from "@/lib/imageUtils";
import api from "@/lib/api";

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.get("/admin/stats");
      setStats(res.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("You don't have admin access. Contact support if you believe this is an error.");
      } else {
        setError("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ShieldX className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-stone-900 mb-2">Access Denied</h1>
          <p className="text-stone-500 mb-4">{error}</p>
          <Button onClick={() => navigate("/dashboard")} variant="outline">Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-stone-900 text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-emerald-400" />
              <div>
                <h1 className="text-xl font-bold">Admin Panel</h1>
                <p className="text-sm text-stone-400">Paradise Borrowed™ Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-stone-400">Logged in as {user?.email}</span>
              <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")} className="text-stone-900">
                Exit Admin
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-stone-200 p-1 rounded-xl mb-8">
            <TabsTrigger value="overview" className="rounded-lg data-[state=active]:bg-stone-900 data-[state=active]:text-white">
              <TrendingUp className="w-4 h-4 mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-stone-900 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" /> Users
            </TabsTrigger>
            <TabsTrigger value="items" className="rounded-lg data-[state=active]:bg-stone-900 data-[state=active]:text-white">
              <Package className="w-4 h-4 mr-2" /> Listings
            </TabsTrigger>
            <TabsTrigger value="bookings" className="rounded-lg data-[state=active]:bg-stone-900 data-[state=active]:text-white">
              <Calendar className="w-4 h-4 mr-2" /> Bookings
            </TabsTrigger>
            <TabsTrigger value="waitlist" className="rounded-lg data-[state=active]:bg-coral data-[state=active]:text-white">
              <Mail className="w-4 h-4 mr-2" /> Waitlist
            </TabsTrigger>
            {/* ID Verify tab hidden — moved to V2 */}
            <TabsTrigger value="payouts" className="rounded-lg data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="admin-tab-payouts">
              <Wallet className="w-4 h-4 mr-2" /> Payouts
            </TabsTrigger>
            <TabsTrigger value="promos" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-white" data-testid="admin-tab-promos">
              <Award className="w-4 h-4 mr-2" /> Promos
            </TabsTrigger>
            <TabsTrigger value="refunds" className="rounded-lg data-[state=active]:bg-red-600 data-[state=active]:text-white" data-testid="admin-tab-refunds">
              <RotateCcw className="w-4 h-4 mr-2" /> Refunds
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard icon={Users} label="Total Users" value={stats?.users?.total || 0} subvalue={`${stats?.users?.total || 0} registered`} color="blue" />
              <StatCard icon={Package} label="Listings" value={stats?.items?.total || 0} subvalue={`${stats?.items?.user_created || 0} user-created`} color="emerald" />
              <StatCard icon={Calendar} label="Bookings" value={stats?.bookings?.total || 0} subvalue={`${stats?.bookings?.pending || 0} pending`} color="amber" />
              <StatCard icon={DollarSign} label="Platform Revenue" value={`$${stats?.revenue?.total || 0}`} subvalue="7% fees collected" color="green" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={MessageSquare} label="Messages" value={stats?.messages || 0} color="purple" />
              <StatCard icon={Star} label="Reviews" value={stats?.reviews || 0} color="orange" />
              <StatCard icon={Package} label="Demo Items" value={stats?.items?.demo || 0} color="stone" />
              <StatCard icon={Shield} label="Admin Users" value="3" color="red" />
            </div>
            
            {/* Quick Actions */}
            <div className="mt-8 bg-white rounded-xl border border-stone-200 p-6">
              <h3 className="font-semibold text-stone-900 mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                <SeedReviewsButton onSuccess={fetchStats} />
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <UsersPanel />
          </TabsContent>

          {/* Items Tab */}
          <TabsContent value="items">
            <ItemsPanel />
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <BookingsPanel />
          </TabsContent>

          {/* Waitlist Tab */}
          <TabsContent value="waitlist">
            <WaitlistPanel />
          </TabsContent>

          {/* ID Verifications Tab — hidden, moved to V2 */}

          {/* Payouts Tab */}
          <TabsContent value="payouts">
            <PayoutsPanel />
          </TabsContent>

          {/* Promos Tab */}
          <TabsContent value="promos">
            <PromosPanel />
          </TabsContent>

          {/* Refunds Tab */}
          <TabsContent value="refunds">
            <RefundsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subvalue, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    green: "bg-green-50 text-green-600",
    purple: "bg-purple-50 text-purple-600",
    orange: "bg-coral-light text-coral",
    stone: "bg-stone-100 text-stone-600",
    red: "bg-red-50 text-red-600",
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 p-4">
      <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-stone-900">{value}</p>
      <p className="text-sm text-stone-500">{label}</p>
      {subvalue && <p className="text-xs text-stone-400 mt-1">{subvalue}</p>}
    </div>
  );
}

function PayoutStatusBadge({ user }) {
  // Task 2B — match the table in deliverables doc exactly
  if (user.payout_requires_manual_processing) {
    return <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs rounded-full" data-testid={`payout-status-caribbean-${user.id}`}>Manual (Caribbean)</Badge>;
  }
  if (!user.stripe_account_id) {
    return <Badge className="bg-stone-100 text-stone-600 border-stone-200 text-xs rounded-full" data-testid={`payout-status-needs-setup-${user.id}`}>Not set up</Badge>;
  }
  if (!user.stripe_onboarding_complete) {
    return <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs rounded-full" data-testid={`payout-status-onboarding-${user.id}`}>Onboarding incomplete</Badge>;
  }
  if (!user.stripe_payouts_enabled) {
    return <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs rounded-full" data-testid={`payout-status-verifying-${user.id}`}>Verification pending</Badge>;
  }
  return <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs rounded-full" data-testid={`payout-status-connected-${user.id}`}>Connected</Badge>;
}


function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [payoutFilter, setPayoutFilter] = useState("all");

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, payoutFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10, search: search || undefined };
      if (payoutFilter !== "all") params.payout_status = payoutFilter;
      const res = await api.get("/admin/users", { params });
      setUsers(res.data.users);
      setTotalPages(res.data.pages);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const toggleVerification = async (userId, currentStatus) => {
    try {
      await api.put(`/admin/users/${userId}`, { is_verified: !currentStatus });
      toast.success("User updated");
      fetchUsers();
    } catch (err) {
      toast.error("Failed to update user");
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200">
      <div className="p-4 border-b border-stone-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="font-semibold text-stone-900">User Management</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={payoutFilter}
            onChange={(e) => { setPayoutFilter(e.target.value); setPage(1); }}
            className="h-9 rounded-md border border-stone-200 text-sm px-2 bg-white"
            data-testid="payout-status-filter"
          >
            <option value="all">All users</option>
            <option value="needs_setup">Needs payout setup</option>
            <option value="onboarding">Onboarding incomplete</option>
            <option value="verifying">Verification pending</option>
            <option value="caribbean">Caribbean (manual)</option>
            <option value="connected">Fully connected</option>
          </select>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Search users..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9"
            />
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="p-8 text-center text-stone-500">Loading...</div>
      ) : (
        <div className="divide-y divide-stone-100">
          {users.map((u) => (
            <div key={u.id} className="p-4 flex items-center justify-between hover:bg-stone-50">
              <div className="flex items-center gap-3">
                <Avatar className="w-10 h-10">
                  <AvatarFallback className="bg-stone-100">{u.name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-stone-900">{u.name}</p>
                    {/* ID verify badge hidden — V2 */}
                    {u.is_early_adopter && <Badge variant="outline" className="text-xs">Early Adopter</Badge>}
                    <PayoutStatusBadge user={u} />
                  </div>
                  <p className="text-sm text-stone-500">{u.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-stone-400">{u.item_count} listings · {u.booking_count} bookings</span>
                {/* ID verify toggle hidden — V2 */}
                <Button variant="ghost" size="sm" onClick={() => setSelectedUser(u)}>
                  <Eye className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Pagination */}
      <div className="p-4 border-t border-stone-200 flex items-center justify-between">
        <span className="text-sm text-stone-500">Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* User Detail Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarFallback>{selectedUser.name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{selectedUser.name}</p>
                  <p className="text-sm text-stone-500">{selectedUser.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-stone-500">Location:</span> {selectedUser.location || "N/A"}</div>
                {/* ID verified field hidden — V2 */}
                <div><span className="text-stone-500">Early Adopter:</span> {selectedUser.is_early_adopter ? "Yes" : "No"}</div>
                <div><span className="text-stone-500">Joined:</span> {new Date(selectedUser.created_at).toLocaleDateString()}</div>
              </div>
              {selectedUser.id_document_url && (
                <div>
                  <p className="text-sm font-medium mb-2">ID Document</p>
                  <img src={getImageUrl(selectedUser.id_document_url)} alt="ID" className="w-full rounded-lg border" />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ItemsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchItems();
  }, [page, search, filter]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10, search: search || undefined };
      if (filter === "demo") params.demo_only = true;
      if (filter === "for_sale") params.for_sale_only = true;
      const res = await api.get("/admin/items", { params });
      setItems(res.data.items);
      setTotalPages(res.data.pages);
    } catch (err) {
      toast.error("Failed to load items");
    } finally {
      setLoading(false);
    }
  };

  const toggleDemoFlag = async (itemId, currentStatus) => {
    try {
      await api.put(`/admin/items/${itemId}`, { is_demo_item: !currentStatus });
      toast.success("Item updated");
      fetchItems();
    } catch (err) {
      toast.error("Failed to update item");
    }
  };

  const deleteItem = async (itemId) => {
    if (!confirm("Are you sure you want to delete this item?")) return;
    try {
      await api.delete(`/admin/items/${itemId}`);
      toast.success("Item deleted");
      fetchItems();
    } catch (err) {
      toast.error("Failed to delete item");
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200">
      <div className="p-4 border-b border-stone-200 flex items-center justify-between flex-wrap gap-4">
        <h2 className="font-semibold text-stone-900">Listing Management</h2>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Items</SelectItem>
              <SelectItem value="demo">Demo Only</SelectItem>
              <SelectItem value="for_sale">For Sale</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input placeholder="Search..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9" />
          </div>
        </div>
      </div>
      
      {loading ? (
        <div className="p-8 text-center text-stone-500">Loading...</div>
      ) : (
        <div className="divide-y divide-stone-100">
          {items.map((item) => (
            <div key={item.id} className="p-4 flex items-center justify-between hover:bg-stone-50">
              <div className="flex items-center gap-3">
                <img src={getImageUrl(item.images?.[0])} alt="" className="w-12 h-12 rounded-lg object-cover bg-stone-100" />
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-stone-900">{item.title}</p>
                    {item.is_demo_item && <Badge className="bg-amber-100 text-amber-700 text-xs">Demo</Badge>}
                    {item.for_sale && <Badge className="bg-emerald-100 text-emerald-700 text-xs">For Sale</Badge>}
                  </div>
                  <p className="text-sm text-stone-500">{item.owner?.name} · {item.location}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-stone-700">${item.price_per_day}/day</span>
                <Button variant="ghost" size="sm" onClick={() => toggleDemoFlag(item.id, item.is_demo_item)}>
                  <AlertTriangle className={`w-4 h-4 ${item.is_demo_item ? "text-amber-500" : "text-stone-300"}`} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => deleteItem(item.id)} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="p-4 border-t border-stone-200 flex items-center justify-between">
        <span className="text-sm text-stone-500">Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function BookingsPanel() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchBookings();
  }, [page, statusFilter]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("/admin/bookings", { params });
      setBookings(res.data.bookings);
      setTotalPages(res.data.pages);
    } catch (err) {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, status) => {
    try {
      await api.put(`/admin/bookings/${bookingId}`, { status });
      toast.success("Booking updated");
      fetchBookings();
    } catch (err) {
      toast.error("Failed to update booking");
    }
  };

  const statusColors = {
    pending: "bg-amber-100 text-amber-700",
    confirmed: "bg-blue-100 text-blue-700",
    completed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-red-100 text-red-700"
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200">
      <div className="p-4 border-b border-stone-200 flex items-center justify-between">
        <h2 className="font-semibold text-stone-900">Booking Management</h2>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {loading ? (
        <div className="p-8 text-center text-stone-500">Loading...</div>
      ) : bookings.length === 0 ? (
        <div className="p-8 text-center text-stone-500">No bookings found</div>
      ) : (
        <div className="divide-y divide-stone-100">
          {bookings.map((b) => (
            <div key={b.id} className="p-4 flex items-center justify-between hover:bg-stone-50">
              <div className="flex items-center gap-3">
                <img src={getImageUrl(b.item?.images?.[0])} alt="" className="w-12 h-12 rounded-lg object-cover bg-stone-100" />
                <div>
                  <p className="font-medium text-stone-900">{b.item?.title || "Unknown Item"}</p>
                  <p className="text-sm text-stone-500">
                    {b.borrower?.name} → {b.lender?.name}
                  </p>
                  <p className="text-xs text-stone-400">
                    {new Date(b.start_date).toLocaleDateString()} - {new Date(b.end_date).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="font-medium">${b.total_price?.toFixed(2)}</p>
                  <p className="text-xs text-stone-400">Fee: ${b.platform_fee?.toFixed(2)}</p>
                </div>
                <Select value={b.status} onValueChange={(status) => updateStatus(b.id, status)}>
                  <SelectTrigger className={`w-28 text-xs ${statusColors[b.status] || ""}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                {b.payment_status === 'paid' && b.refund_status !== 'refunded' && (
                  <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200 hover:bg-red-50 gap-1" onClick={async () => {
                    if (!window.confirm(`Issue refund of $${b.total_price?.toFixed(2)} for this booking?`)) return;
                    try {
                      await api.post(`/admin/refunds/${b.id}`);
                      toast.success("Refund processed");
                      fetchBookings();
                    } catch (err) { toast.error(err.response?.data?.detail || "Refund failed"); }
                  }} data-testid={`admin-refund-${b.id}`}>
                    <RotateCcw className="w-3 h-3" /> Refund
                  </Button>
                )}
                {b.refund_status === 'refunded' && (
                  <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200">Refunded</Badge>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="p-4 border-t border-stone-200 flex items-center justify-between">
        <span className="text-sm text-stone-500">Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function VerificationsPanel() {
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVerifications();
  }, []);

  const fetchVerifications = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/id-verifications");
      setVerifications(res.data.verifications);
    } catch (err) {
      toast.error("Failed to load verifications");
    } finally {
      setLoading(false);
    }
  };

  const approve = async (userId) => {
    try {
      await api.post(`/admin/id-verifications/${userId}/approve`);
      toast.success("Verification approved");
      fetchVerifications();
    } catch (err) {
      toast.error("Failed to approve");
    }
  };

  const reject = async (userId) => {
    try {
      await api.post(`/admin/id-verifications/${userId}/reject`);
      toast.success("Verification rejected");
      fetchVerifications();
    } catch (err) {
      toast.error("Failed to reject");
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200">
      <div className="p-4 border-b border-stone-200">
        <h2 className="font-semibold text-stone-900">Pending ID Verifications</h2>
      </div>
      
      {loading ? (
        <div className="p-8 text-center text-stone-500">Loading...</div>
      ) : verifications.length === 0 ? (
        <div className="p-8 text-center text-stone-500">
          <ShieldCheck className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
          <p>No pending verifications</p>
        </div>
      ) : (
        <div className="divide-y divide-stone-100">
          {verifications.map((v) => (
            <div key={v.id} className="p-4">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback>{v.name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-stone-900">{v.name}</p>
                    <p className="text-sm text-stone-500">{v.email}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => approve(v.id)} className="bg-emerald-600 hover:bg-emerald-700">
                    <CheckCircle className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => reject(v.id)} className="text-red-600 border-red-200 hover:bg-red-50">
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              </div>
              {v.id_document_url && (
                <div className="mt-3">
                  <p className="text-xs text-stone-500 mb-2">Uploaded ID Document:</p>
                  <img src={getImageUrl(v.id_document_url)} alt="ID Document" className="max-w-md rounded-lg border border-stone-200" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SeedReviewsButton({ onSuccess }) {
  const [loading, setLoading] = useState(false);

  const seedReviews = async () => {
    setLoading(true);
    try {
      const res = await api.post("/admin/seed-demo-reviews");
      if (res.data.seeded) {
        toast.success(res.data.message);
        onSuccess?.();
      } else {
        toast.info(res.data.message);
      }
    } catch (err) {
      toast.error("Failed to seed reviews");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button 
      onClick={seedReviews} 
      disabled={loading}
      className="bg-coral hover:bg-[#d44814]"
    >
      <Star className="w-4 h-4 mr-2" />
      {loading ? "Seeding..." : "Seed Demo Reviews"}
    </Button>
  );
}

// Waitlist Panel - View emails by location
function WaitlistPanel() {
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWaitlistData();
  }, []);

  const fetchWaitlistData = async () => {
    try {
      const res = await api.get("/admin/waitlist", { params: { limit: 500 } });
      const data = res.data;
      // Build stats from the response
      setStats({
        total_signups: data.total || 0,
        by_location: (data.location_stats || []).map(s => ({ location: s._id, count: s.count }))
      });
      setEntries(data.entries || []);
    } catch (err) {
      toast.error("Failed to load waitlist data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationEntries = async (location) => {
    setSelectedLocation(location);
    try {
      const res = await api.get("/admin/waitlist", { params: { location, limit: 500 } });
      setEntries(res.data.entries || []);
    } catch (err) {
      toast.error("Failed to load entries");
    }
  };

  const exportToCsv = () => {
    if (!entries.length) return;
    
    const csv = [
      ["Email", "Location", "Signed Up"],
      ...entries.map(e => [e.email, e.location, e.created_at])
    ].map(row => row.join(",")).join("\n");
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `waitlist-${selectedLocation?.toLowerCase().replace(/\s+/g, "-") || "all"}.csv`;
    a.click();
  };

  if (loading) {
    return <div className="text-center py-8">Loading waitlist data...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="bg-white rounded-xl border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-stone-900">Waitlist Overview</h3>
            <p className="text-sm text-stone-500">Email signups for coming soon locations</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-coral">{stats?.total_signups || 0}</p>
            <p className="text-sm text-stone-500">Total signups</p>
          </div>
        </div>

        {stats?.by_location?.length > 0 ? (
          <div className="space-y-3">
            <p className="text-sm font-medium text-stone-700 mb-2">Signups by Location (click to view emails):</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {stats.by_location.map((loc) => (
                <button
                  key={loc.location}
                  onClick={() => fetchLocationEntries(loc.location)}
                  className={`p-3 rounded-lg border text-left transition-all ${
                    selectedLocation === loc.location 
                      ? "border-coral bg-coral-light" 
                      : "border-stone-200 hover:border-coral hover:bg-stone-50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-coral" />
                    <span className="font-medium text-stone-900 text-sm">{loc.location}</span>
                  </div>
                  <p className="text-lg font-bold text-coral mt-1">{loc.count}</p>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-stone-500">
            <Mail className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p>No waitlist signups yet</p>
            <p className="text-sm mt-1">Signups will appear here when users join the waitlist for coming soon locations</p>
          </div>
        )}
      </div>

      {/* Email List */}
      {selectedLocation && (
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-stone-900">
                {selectedLocation} Waitlist
              </h3>
              <p className="text-sm text-stone-500">{entries.length} people waiting</p>
            </div>
            <Button onClick={exportToCsv} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 sticky top-0">
                <tr>
                  <th className="text-left p-3 font-medium text-stone-600">Email</th>
                  <th className="text-left p-3 font-medium text-stone-600">Signed Up</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {entries.map((entry, idx) => (
                  <tr key={idx} className="hover:bg-stone-50">
                    <td className="p-3">
                      <a href={`mailto:${entry.email}`} className="text-coral hover:underline">
                        {entry.email}
                      </a>
                    </td>
                    <td className="p-3 text-stone-500">
                      {new Date(entry.created_at).toLocaleDateString()} {new Date(entry.created_at).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function PromosPanel() {
  const [stats, setStats] = useState(null);
  const [locations, setLocations] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [revokeReason, setRevokeReason] = useState("tos_violation");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, locsRes, usersRes] = await Promise.all([
        api.get("/admin/promo/stats"),
        api.get("/admin/promo/locations"),
        api.get("/admin/promo/eligible-users"),
      ]);
      setStats(statsRes.data);
      setLocations(locsRes.data.locations || []);
      setEligibleUsers(usersRes.data.users || []);
    } catch (err) {
      toast.error("Failed to load promo data");
    } finally {
      setLoading(false);
    }
  };

  const revokePromo = async (userId) => {
    if (!window.confirm("Are you sure you want to revoke this user's promo eligibility? This will set all their listings to 7% fee.")) return;
    setRevoking(userId);
    try {
      const res = await api.post("/admin/promo/revoke", { user_id: userId, reason: revokeReason });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke promo");
    } finally {
      setRevoking(null);
    }
  };

  if (loading) {
    return <div className="bg-white rounded-xl border border-stone-200 p-8 text-center text-stone-500">Loading promo data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-promos-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center mb-3">
            <MapPin className="w-5 h-5" />
          </div>
          <p className="text-2xl font-bold text-stone-900">{stats?.total_locations_with_promos || 0}</p>
          <p className="text-sm text-stone-500">Locations Active</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
            <Users className="w-5 h-5" />
          </div>
          <p className="text-2xl font-bold text-stone-900">{stats?.total_eligible_users || 0}</p>
          <p className="text-sm text-stone-500">Eligible Users</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
            <Package className="w-5 h-5" />
          </div>
          <p className="text-2xl font-bold text-stone-900">{stats?.total_waived_listings || 0}</p>
          <p className="text-sm text-stone-500">Fee-Waived Listings</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center mb-3">
            <XCircle className="w-5 h-5" />
          </div>
          <p className="text-2xl font-bold text-stone-900">{stats?.total_revoked_users || 0}</p>
          <p className="text-sm text-stone-500">Revoked</p>
        </div>
      </div>

      {/* Location Slots Table */}
      <div className="bg-white rounded-xl border border-stone-200">
        <div className="p-4 border-b border-stone-200">
          <h2 className="font-semibold text-stone-900">Location Promo Slots</h2>
          <p className="text-sm text-stone-500 mt-1">First 10 users per location who publish a listing</p>
        </div>
        {locations.length === 0 ? (
          <div className="p-8 text-center text-stone-500">
            <Award className="w-10 h-10 mx-auto text-stone-300 mb-3" />
            <p>No promo slots claimed yet</p>
            <p className="text-sm mt-1">Slots are claimed when users publish their first listing</p>
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {locations.map((loc) => (
              <div key={loc.location_name} className="p-4" data-testid={`promo-location-${loc.location_name}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-amber-500" />
                    <span className="font-medium text-stone-900">{loc.location_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-stone-100 rounded-full h-2 overflow-hidden">
                      <div className="bg-amber-500 h-full rounded-full transition-all" style={{ width: `${(loc.slots_claimed / loc.max_slots) * 100}%` }} />
                    </div>
                    <span className="text-sm font-medium text-stone-600">{loc.slots_claimed}/{loc.max_slots}</span>
                  </div>
                </div>
                {loc.promo_users?.length > 0 && (
                  <div className="ml-7 flex flex-wrap gap-2">
                    {loc.promo_users.map((u) => (
                      <span key={u.id} className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-full border border-amber-200">
                        {u.name} ({u.email})
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Eligible Users with Revoke */}
      <div className="bg-white rounded-xl border border-stone-200">
        <div className="p-4 border-b border-stone-200">
          <h2 className="font-semibold text-stone-900">Eligible Users</h2>
          <p className="text-sm text-stone-500 mt-1">Users with promotional fee waiver — revoke for ToS violations or bans</p>
        </div>
        {eligibleUsers.length === 0 ? (
          <div className="p-8 text-center text-stone-500">No eligible users yet</div>
        ) : (
          <div className="divide-y divide-stone-100">
            {eligibleUsers.map((u) => (
              <div key={u.id} className="p-4 flex items-center justify-between hover:bg-stone-50" data-testid={`promo-user-${u.id}`}>
                <div className="flex items-center gap-3">
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className="bg-amber-100 text-amber-700">{u.name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-stone-900">{u.name}</p>
                    <p className="text-sm text-stone-500">{u.email} &middot; {u.promo_location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right text-sm">
                    <p className="text-stone-600">Listings: {u.total_listings} ({u.waived_listings} waived)</p>
                    <p className="text-xs text-stone-400">Remaining waived: {u.remaining_waived}</p>
                  </div>
                  <Select value={revokeReason} onValueChange={setRevokeReason}>
                    <SelectTrigger className="w-36 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tos_violation">ToS Violation</SelectItem>
                      <SelectItem value="permanent_ban">Permanent Ban</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50 text-xs gap-1"
                    disabled={revoking === u.id}
                    onClick={() => revokePromo(u.id)}
                    data-testid={`revoke-promo-${u.id}`}
                  >
                    <XCircle className="w-3 h-3" />
                    {revoking === u.id ? "Revoking..." : "Revoke"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PayoutsPanel() {
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [completedPayouts, setCompletedPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [activeTab, setActiveTab] = useState("pending");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pendingRes, completedRes] = await Promise.all([
        api.get("/admin/payouts/pending"),
        api.get("/admin/payouts", { params: { limit: 50 } })
      ]);
      setPendingPayouts(pendingRes.data.bookings || []);
      setCompletedPayouts(completedRes.data.payouts || []);
    } catch (err) {
      toast.error("Failed to load payouts");
    } finally {
      setLoading(false);
    }
  };

  const processPayout = async (bookingId) => {
    setProcessing(bookingId);
    try {
      const res = await api.post(`/admin/payouts/${bookingId}`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to process payout");
    } finally {
      setProcessing(null);
    }
  };

  const totalPending = pendingPayouts.reduce((sum, b) => sum + (b.payout_amount || 0), 0);
  const totalCompleted = completedPayouts.filter(p => p.status === "completed").reduce((sum, p) => sum + (p.payout_amount || 0), 0);
  const totalFees = completedPayouts.filter(p => p.status === "completed").reduce((sum, p) => sum + (p.platform_fee || 0), 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center mb-3">
            <Clock className="w-5 h-5" />
          </div>
          <p className="text-2xl font-bold text-stone-900">${totalPending.toFixed(2)}</p>
          <p className="text-sm text-stone-500">Pending Payouts</p>
          <p className="text-xs text-stone-400 mt-1">{pendingPayouts.length} booking(s)</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
            <CheckCircle className="w-5 h-5" />
          </div>
          <p className="text-2xl font-bold text-stone-900">${totalCompleted.toFixed(2)}</p>
          <p className="text-sm text-stone-500">Paid Out</p>
          <p className="text-xs text-stone-400 mt-1">{completedPayouts.filter(p => p.status === "completed").length} payout(s)</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-200 p-4">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
            <DollarSign className="w-5 h-5" />
          </div>
          <p className="text-2xl font-bold text-stone-900">${totalFees.toFixed(2)}</p>
          <p className="text-sm text-stone-500">Platform Revenue (7%)</p>
          <p className="text-xs text-stone-400 mt-1">From completed payouts</p>
        </div>
      </div>

      {/* Tab Toggle */}
      <div className="flex gap-2">
        <Button
          variant={activeTab === "pending" ? "default" : "outline"}
          size="sm"
          className="rounded-full"
          onClick={() => setActiveTab("pending")}
          data-testid="payouts-pending-tab"
        >
          Pending ({pendingPayouts.length})
        </Button>
        <Button
          variant={activeTab === "history" ? "default" : "outline"}
          size="sm"
          className="rounded-full"
          onClick={() => setActiveTab("history")}
          data-testid="payouts-history-tab"
        >
          History ({completedPayouts.length})
        </Button>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-stone-200 p-8 text-center text-stone-500">Loading...</div>
      ) : activeTab === "pending" ? (
        /* Pending Payouts */
        <div className="bg-white rounded-xl border border-stone-200">
          <div className="p-4 border-b border-stone-200">
            <h2 className="font-semibold text-stone-900">Pending Payouts</h2>
            <p className="text-xs text-stone-500 mt-1">Returned & paid bookings awaiting lender payout</p>
          </div>
          {pendingPayouts.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle className="w-10 h-10 mx-auto text-emerald-300 mb-3" />
              <p className="text-stone-500">All caught up! No pending payouts.</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {pendingPayouts.map((b) => (
                <div key={b.id} className="p-4 flex items-center justify-between hover:bg-stone-50" data-testid={`pending-payout-${b.id}`}>
                  <div className="flex items-center gap-3">
                    <img src={getImageUrl(b.item?.images?.[0])} alt="" className="w-12 h-12 rounded-lg object-cover bg-stone-100" />
                    <div>
                      <p className="font-medium text-stone-900">{b.item?.title || "Unknown Item"}</p>
                      <p className="text-sm text-stone-500">
                        <ArrowRight className="w-3 h-3 inline" /> {b.lender?.name || "Unknown"}
                      </p>
                      <p className="text-xs text-stone-400">{b.start_date} — {b.end_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-semibold text-emerald-600">${b.payout_amount?.toFixed(2)}</p>
                      <p className="text-xs text-stone-400">Fee: ${b.platform_fee?.toFixed(2)} | Total: ${b.total_price?.toFixed(2)}</p>
                    </div>
                    <span className="text-xs bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full border border-amber-200">Payouts pending integration</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Payout History */
        <div className="bg-white rounded-xl border border-stone-200">
          <div className="p-4 border-b border-stone-200">
            <h2 className="font-semibold text-stone-900">Payout History</h2>
          </div>
          {completedPayouts.length === 0 ? (
            <div className="p-8 text-center text-stone-500">No payouts processed yet</div>
          ) : (
            <div className="divide-y divide-stone-100">
              {completedPayouts.map((p) => (
                <div key={p.id} className="p-4 flex items-center justify-between hover:bg-stone-50" data-testid={`payout-history-${p.id}`}>
                  <div className="flex items-center gap-3">
                    <img src={getImageUrl(p.item?.images?.[0])} alt="" className="w-10 h-10 rounded-lg object-cover bg-stone-100" />
                    <div>
                      <p className="font-medium text-sm text-stone-900">{p.lender_name || "Unknown"}</p>
                      <p className="text-xs text-stone-500">{p.lender_email}</p>
                      <p className="text-xs text-stone-400">{new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-semibold text-sm text-emerald-600">${p.payout_amount?.toFixed(2)}</p>
                      <p className="text-xs text-stone-400">Fee: ${p.platform_fee?.toFixed(2)}</p>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                      p.status === "completed" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                      p.status === "failed" ? "bg-red-50 text-red-700 border border-red-200" :
                      "bg-amber-50 text-amber-700 border border-amber-200"
                    }`}>
                      {p.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RefundsPanel() {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/admin/refunds");
        setRefunds(res.data.refunds || []);
      } catch { /* empty */ } finally { setLoading(false); }
    })();
  }, []);

  const statusBadge = (status) => {
    if (status === 'refunded') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (status === 'failed') return 'bg-red-50 text-red-700 border-red-200';
    if (status === 'not_eligible') return 'bg-stone-100 text-stone-600 border-stone-200';
    return 'bg-amber-50 text-amber-700 border-amber-200';
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200" data-testid="admin-refunds-panel">
      <div className="p-4 border-b border-stone-200">
        <h2 className="font-semibold text-stone-900">Refund History</h2>
        <p className="text-sm text-stone-500 mt-1">All refund records — automatic cancellations and admin-issued refunds</p>
      </div>
      {loading ? (
        <div className="p-8 text-center text-stone-500">Loading...</div>
      ) : refunds.length === 0 ? (
        <div className="p-12 text-center">
          <RotateCcw className="w-10 h-10 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500 font-medium">No refunds yet</p>
          <p className="text-sm text-stone-400 mt-1">Refunds will appear here when bookings are cancelled or manually refunded.</p>
        </div>
      ) : (
        <div className="divide-y divide-stone-100">
          {refunds.map((r) => (
            <div key={r.id} className="p-4 flex items-center justify-between hover:bg-stone-50" data-testid={`refund-${r.id}`}>
              <div>
                <p className="font-medium text-sm text-stone-900">Booking: {r.booking_id?.slice(0, 8)}...</p>
                <p className="text-xs text-stone-500 mt-0.5">
                  {r.payment_method?.toUpperCase()} &middot; {r.is_admin_refund ? 'Admin refund' : 'User cancellation'} &middot; {new Date(r.created_at).toLocaleString()}
                </p>
                {r.error && <p className="text-xs text-red-500 mt-0.5">{r.error}</p>}
              </div>
              <div className="flex items-center gap-3">
                <p className="font-semibold text-sm">${r.amount?.toFixed(2)}</p>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium border ${statusBadge(r.status)}`}>
                  {r.status === 'not_eligible' ? 'Not Eligible' : r.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


